/**
 * DTC Platform Settings
 * Admin page for managing DTC platform connections (Shopify, Amazon)
 */

import React, { useState } from 'react'
import { Plus, RefreshCw, Trash2, CheckCircle, XCircle, Clock, AlertCircle } from 'lucide-react'
import { useDtcPlatforms, useDtcSyncStatus, useTriggerDtcSync, getSyncStatusColor, getPlatformTypeDisplay } from '../../lib/dtc-data-service'
import DtcHeader from '../../components/DtcHeader'

export default function PlatformSettings() {
  const { data: platforms, isLoading, refetch } = useDtcPlatforms()
  const [selectedPlatformId, setSelectedPlatformId] = useState<string | null>(null)
  const [showAddPlatform, setShowAddPlatform] = useState(false)

  const selectedPlatform = platforms?.find(p => p.id === selectedPlatformId)

  return (
    <div>
      <DtcHeader
        title="DTC Platform Settings"
        subtitle="Manage Shopify and Amazon connections"
        onRefresh={refetch}
        isLoading={isLoading}
        platforms={platforms}
        selectedPlatformId={selectedPlatformId}
        onPlatformChange={setSelectedPlatformId}
      />
      
      <div className="p-6 space-y-6">

      {/* Connected Platforms List */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Connected Platforms</h3>
          <button
            onClick={() => setShowAddPlatform(true)}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            <Plus className="w-4 h-4" />
            Add Platform
          </button>
        </div>

        <div className="divide-y divide-gray-200">
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">
              <RefreshCw className="w-8 h-8 mx-auto animate-spin mb-2" />
              Loading platforms...
            </div>
          ) : platforms && platforms.length > 0 ? (
            platforms.map((platform) => (
              <PlatformCard
                key={platform.id}
                platform={platform}
                onSelect={() => setSelectedPlatformId(platform.id)}
                isSelected={selectedPlatformId === platform.id}
              />
            ))
          ) : (
            <div className="p-8 text-center">
              <AlertCircle className="w-12 h-12 mx-auto text-gray-400 mb-3" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Platforms Connected</h3>
              <p className="text-gray-600 mb-4">Connect your first DTC platform to get started</p>
              <button
                onClick={() => setShowAddPlatform(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              >
                <Plus className="w-4 h-4" />
                Connect Platform
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Platform Detail Panel */}
      {selectedPlatform && (
        <PlatformDetailPanel
          platform={selectedPlatform}
          onClose={() => setSelectedPlatformId(null)}
        />
      )}

      {/* Add Platform Modal */}
      {showAddPlatform && (
        <AddPlatformModal
          onClose={() => setShowAddPlatform(false)}
          onSuccess={() => {
            setShowAddPlatform(false)
            refetch()
          }}
        />
      )}
      </div>
    </div>
  )
}

// Platform Card Component
function PlatformCard({ platform, onSelect, isSelected }: any) {
  const syncStatusColor = getSyncStatusColor(platform.sync_status)
  const statusIcons = {
    success: CheckCircle,
    syncing: RefreshCw,
    error: XCircle,
    pending: Clock
  }
  const StatusIcon = statusIcons[platform.sync_status as keyof typeof statusIcons] || Clock

  return (
    <div
      onClick={onSelect}
      className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
        isSelected ? 'bg-indigo-50 border-l-4 border-indigo-600' : ''
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <h4 className="text-sm font-semibold text-gray-900">{platform.platform_name}</h4>
          <p className="text-xs text-gray-600 mt-1">
            {getPlatformTypeDisplay(platform.platform_type)}
          </p>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <StatusIcon className={`w-4 h-4 text-${syncStatusColor}-600 ${platform.sync_status === 'syncing' ? 'animate-spin' : ''}`} />
            <span className="text-xs text-gray-600 capitalize">{platform.sync_status}</span>
          </div>

          {platform.last_sync_at && (
            <span className="text-xs text-gray-500">
              Last sync: {new Date(platform.last_sync_at).toLocaleString()}
            </span>
          )}
        </div>
      </div>

      {platform.sync_status === 'error' && platform.sync_error_message && (
        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
          {platform.sync_error_message}
        </div>
      )}
    </div>
  )
}

// Platform Detail Panel
function PlatformDetailPanel({ platform, onClose }: any) {
  const { data: syncStatus } = useDtcSyncStatus(platform.id)
  const triggerSync = useTriggerDtcSync()
  const [isSyncing, setIsSyncing] = useState(false)

  const handleSync = async (fullSync: boolean) => {
    setIsSyncing(true)
    try {
      await triggerSync.mutateAsync({
        platform_id: platform.id,
        full_sync: fullSync
      })
      alert('Sync triggered successfully!')
    } catch (error) {
      alert(`Sync failed: ${error}`)
    } finally {
      setIsSyncing(false)
    }
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">{platform.platform_name}</h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600"
        >
          ✕
        </button>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">Platform Info</h4>
          <dl className="space-y-2 text-sm">
            <div>
              <dt className="text-gray-600">Type:</dt>
              <dd className="font-medium">{getPlatformTypeDisplay(platform.platform_type)}</dd>
            </div>
            <div>
              <dt className="text-gray-600">Status:</dt>
              <dd className="font-medium capitalize">{platform.sync_status}</dd>
            </div>
            <div>
              <dt className="text-gray-600">Last Sync:</dt>
              <dd className="font-medium">
                {platform.last_sync_at
                  ? new Date(platform.last_sync_at).toLocaleString()
                  : 'Never'}
              </dd>
            </div>
          </dl>
        </div>

        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">Data Coverage</h4>
          {syncStatus?.data_coverage && (
            <dl className="space-y-2 text-sm">
              <div>
                <dt className="text-gray-600">Date Range:</dt>
                <dd className="font-medium">
                  {syncStatus.data_coverage.earliest_date || 'N/A'} to{' '}
                  {syncStatus.data_coverage.latest_date || 'N/A'}
                </dd>
              </div>
              <div>
                <dt className="text-gray-600">Total Days:</dt>
                <dd className="font-medium">{syncStatus.data_coverage.total_days}</dd>
              </div>
              <div>
                <dt className="text-gray-600">Total Orders:</dt>
                <dd className="font-medium">{syncStatus.data_coverage.total_orders?.toLocaleString()}</dd>
              </div>
              <div>
                <dt className="text-gray-600">Total Revenue:</dt>
                <dd className="font-medium">
                  ${syncStatus.data_coverage.total_revenue?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </dd>
              </div>
            </dl>
          )}
        </div>
      </div>

      <div className="mt-6 pt-6 border-t border-gray-200">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Actions</h4>
        <div className="flex gap-3">
          <button
            onClick={() => handleSync(false)}
            disabled={isSyncing || platform.sync_status === 'syncing'}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-400 flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
            {isSyncing ? 'Syncing...' : 'Sync Now (7 days)'}
          </button>
          <button
            onClick={() => handleSync(true)}
            disabled={isSyncing || platform.sync_status === 'syncing'}
            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-400 flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
            Full Sync (90 days)
          </button>
          <button
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center gap-2 ml-auto"
            onClick={() => {
              if (confirm('Are you sure you want to disconnect this platform?')) {
                alert('Disconnect functionality coming soon')
              }
            }}
          >
            <Trash2 className="w-4 h-4" />
            Disconnect
          </button>
        </div>
      </div>
    </div>
  )
}

// Add Platform Modal (Simplified for now)
function AddPlatformModal({ onClose, onSuccess }: any) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h3 className="text-lg font-semibold mb-4">Add DTC Platform</h3>
        <p className="text-gray-600 mb-4">
          Platform connections are currently managed via database. Use the setup guide in <code className="bg-gray-100 px-1 rounded">/docs/DTC_SETUP_GUIDE.md</code> for instructions.
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

